import { supabase } from "@/integrations/supabase/client";

export interface PaymentSchedule {
  id: string;
  campaign_id: string;
  total_amount: number;
  payment_plan_type: 'full' | 'installments';
  installment_count: number;
  installment_amount?: number;
  currency: string;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface PaymentInstallment {
  id: string;
  schedule_id: string;
  campaign_id: string;
  installment_number: number;
  amount: number;
  due_date: string;
  paid_date?: string;
  status: 'pending' | 'paid' | 'overdue' | 'partially_paid';
  payment_method?: string;
  transaction_reference?: string;
  receipt_url?: string;
  notes?: string;
  reminder_sent_count: number;
  last_reminder_sent?: string;
}

export interface PaymentConfirmation {
  id: string;
  installment_id: string;
  campaign_id: string;
  confirmation_type: 'receipt' | 'screenshot' | 'bank_statement';
  file_url: string;
  amount_confirmed: number;
  payment_date: string;
  bank_reference?: string;
  notes?: string;
  verification_status: 'pending' | 'verified' | 'rejected';
}

export interface CreatePaymentScheduleRequest {
  campaignId: string;
  totalAmount: number;
  planType: 'full' | 'installments';
  installmentCount: number;
  firstDueDate?: string;
}

export const paymentScheduleService = {
  async createPaymentSchedule(request: CreatePaymentScheduleRequest) {
    const { data, error } = await supabase.rpc('create_payment_schedule', {
      p_campaign_id: request.campaignId,
      p_total_amount: request.totalAmount,
      p_plan_type: request.planType,
      p_installment_count: request.installmentCount,
      p_first_due_date: request.firstDueDate || null
    });

    if (error) {
      console.error('Error creating payment schedule:', error);
      throw new Error('Failed to create payment schedule');
    }

    return data;
  },

  async getPaymentSchedule(campaignId: string): Promise<PaymentSchedule | null> {
    const { data, error } = await supabase
      .from('payment_schedules')
      .select('*')
      .eq('campaign_id', campaignId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching payment schedule:', error);
      throw new Error('Failed to fetch payment schedule');
    }

    return data as PaymentSchedule;
  },

  async getPaymentInstallments(scheduleId: string): Promise<PaymentInstallment[]> {
    const { data, error } = await supabase
      .from('payment_installments')
      .select('*')
      .eq('schedule_id', scheduleId)
      .order('installment_number');

    if (error) {
      console.error('Error fetching installments:', error);
      throw new Error('Failed to fetch installments');
    }

    return (data || []) as PaymentInstallment[];
  },

  async getCampaignInstallments(campaignId: string): Promise<PaymentInstallment[]> {
    const { data, error } = await supabase
      .from('payment_installments')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('due_date');

    if (error) {
      console.error('Error fetching campaign installments:', error);
      throw new Error('Failed to fetch campaign installments');
    }

    return (data || []) as PaymentInstallment[];
  },

  async markInstallmentPaid(
    installmentId: string,
    paymentMethod: string = 'bank_transfer',
    transactionReference?: string,
    paidAmount?: number
  ) {
    const { data, error } = await supabase.rpc('mark_installment_paid', {
      p_installment_id: installmentId,
      p_payment_method: paymentMethod,
      p_transaction_reference: transactionReference,
      p_paid_amount: paidAmount
    });

    if (error) {
      console.error('Error marking installment as paid:', error);
      throw new Error('Failed to mark installment as paid');
    }

    return data;
  },

  async uploadPaymentConfirmation(confirmation: Omit<PaymentConfirmation, 'id' | 'verification_status'>) {
    const { data, error } = await supabase
      .from('payment_confirmations')
      .insert({
        ...confirmation,
        submitted_by: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .maybeSingle();

    if (error || !data) {
      console.error('Error uploading payment confirmation:', error);
      throw new Error('Failed to upload payment confirmation');
    }

    return data;
  },

  async getPaymentConfirmations(campaignId: string): Promise<PaymentConfirmation[]> {
    const { data, error } = await supabase
      .from('payment_confirmations')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching payment confirmations:', error);
      throw new Error('Failed to fetch payment confirmations');
    }

    return (data || []) as PaymentConfirmation[];
  },

  async getOverdueInstallments() {
    const { data, error } = await supabase.rpc('get_overdue_installments');

    if (error) {
      console.error('Error fetching overdue installments:', error);
      throw new Error('Failed to fetch overdue installments');
    }

    return data || [];
  },

  async updateInstallmentStatus(installmentId: string, status: string, notes?: string) {
    const { data, error } = await supabase
      .from('payment_installments')
      .update({ 
        status,
        notes,
        updated_at: new Date().toISOString()
      })
      .eq('id', installmentId)
      .select()
      .maybeSingle();

    if (error || !data) {
      console.error('Error updating installment status:', error);
      throw new Error('Failed to update installment status');
    }

    return data;
  }
};